import React, { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { SovrynnLogo } from '../components/Brand';
import PublicLegalFooter from '../components/legal/PublicLegalFooter';
import { CheckCircle2 } from 'lucide-react';
import '../App.css';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwFocused, setPwFocused] = useState(false);
  const [cfFocused, setCfFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [apiError, setApiError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setApiError('');
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setValidationError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      let msg = err.response?.data?.message;
      if (Array.isArray(msg)) msg = msg.join(' ');
      msg = msg || 'Something went wrong.';
      if (/invalid|expired/i.test(String(msg))) {
        setTokenError(true);
      } else {
        setApiError(String(msg));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  if (tokenError) {
    return (
      <div className="login-container-wrapper">
        <div className="login-container">
          <div className="login-content">
            <div className="auth-logo">
              <SovrynnLogo size="lg" />
            </div>
            <h1 className="auth-page-title">Link expired</h1>
            <p className="auth-page-subtitle">
              This reset link is invalid or has expired. Reset links are valid for 1 hour.
            </p>
            <Link to="/forgot-password" className="submit-button auth-link-button">
              Request a new link
            </Link>
          </div>
        </div>
        <PublicLegalFooter />
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container-wrapper">
        <div className="login-container">
          <div className="login-content">
            <div className="auth-logo">
              <SovrynnLogo size="lg" />
            </div>
            <CheckCircle2 className="auth-success-icon" size={56} strokeWidth={2} aria-hidden />
            <h1 className="auth-page-title">Password reset!</h1>
            <p className="auth-page-subtitle">
              Your password has been updated. You can now log in with your new password.
            </p>
            <Link to="/login" className="submit-button auth-link-button">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container-wrapper">
      <div className="login-container">
        <div className="login-content">
          <div className="auth-logo">
            <SovrynnLogo size="lg" />
          </div>
          <h1 className="auth-page-title">Set a new password</h1>
          <p className="auth-page-subtitle">Choose a strong password for your Sovrynn account.</p>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <label
                htmlFor="reset-password"
                className={`form-label ${pwFocused || password ? 'focused' : ''}`}
              >
                New Password
              </label>
              <input
                id="reset-password"
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPwFocused(true)}
                onBlur={() => setPwFocused(password.length > 0)}
                placeholder="Min 8 characters"
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div className="input-group">
              <label
                htmlFor="reset-confirm"
                className={`form-label ${cfFocused || confirm ? 'focused' : ''}`}
              >
                Confirm Password
              </label>
              <input
                id="reset-confirm"
                type="password"
                className="form-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onFocus={() => setCfFocused(true)}
                onBlur={() => setCfFocused(confirm.length > 0)}
                placeholder="Confirm password"
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>

            {(validationError || apiError) && (
              <p className="auth-inline-error">{validationError || apiError}</p>
            )}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="register-link" style={{ marginTop: 20 }}>
              <Link to="/login" className="register-link-text">
                &larr; Back to Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
      <PublicLegalFooter />
    </div>
  );
}
